<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import Avatar from 'next/avatar/Avatar.vue';
import MessageMeta from '../../MessageMeta.vue';
import { MESSAGE_STATUS, MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';

const { contentAttributes, status, sender, messageType, inboxId, avatarInfo } =
  useMessageContext();

const inboxGetter = useMapGetter('inboxes/getInbox');
const currentChat = useMapGetter('getSelectedChat');

const hasError = computed(() => {
  return status.value === MESSAGE_STATUS.FAILED;
});

const isOutgoing = computed(() => messageType.value === MESSAGE_TYPES.OUTGOING);

const fromEmail = computed(() => {
  const [emailAddress] = contentAttributes.value?.email?.from ?? [];
  if (emailAddress) return emailAddress;

  return isOutgoing.value
    ? inboxGetter.value(inboxId.value)?.email
    : sender.value?.email;
});

// A reply sent without touching "Para" carries no recipients of its own: it
// goes to the contact of the conversation.
const toEmail = computed(() => {
  const { toEmails, email } = contentAttributes.value;
  const recipients = email?.to ?? toEmails ?? [];
  if (recipients.length || !isOutgoing.value) return recipients;
  const contactEmail = currentChat.value?.meta?.sender?.email;
  return contactEmail ? [contactEmail] : [];
});

const ccEmail = computed(() => {
  return (
    contentAttributes.value?.ccEmails ??
    contentAttributes.value?.email?.cc ??
    []
  );
});

const bccEmail = computed(() => {
  return (
    contentAttributes.value?.bccEmails ??
    contentAttributes.value?.email?.bcc ??
    []
  );
});

// An incoming email is not always written by the contact of the conversation
// (someone in copy can reply), so the contact is only credited for emails sent
// from the contact's own address.
const author = computed(() => {
  if (isOutgoing.value || fromEmail.value === sender.value?.email) {
    return avatarInfo.value;
  }
  return { name: fromEmail.value ?? '', src: '' };
});

const showFromEmail = computed(
  () => fromEmail.value && fromEmail.value !== author.value.name
);
</script>

<template>
  <section class="flex items-start gap-3 text-sm">
    <Avatar v-bind="author" :size="36" class="flex-shrink-0" />
    <div
      class="flex-1 min-w-0 space-y-0.5 break-words"
      :class="hasError ? 'text-n-ruby-11' : 'text-n-slate-11'"
    >
      <div class="flex flex-wrap items-baseline gap-x-2">
        <span
          class="font-medium"
          :class="hasError ? 'text-n-ruby-11' : 'text-n-slate-12'"
        >
          {{ author.name }}
        </span>
        <span v-if="showFromEmail" class="min-w-0 truncate">
          {{ `<${fromEmail}>` }}
        </span>
      </div>
      <div v-if="toEmail.length">
        {{ $t('EMAIL_HEADER.TO') }}: {{ toEmail.join(', ') }}
      </div>
      <div v-if="ccEmail.length">
        {{ $t('EMAIL_HEADER.CC') }}:
        {{ ccEmail.join(', ') }}
      </div>
      <div v-if="bccEmail.length">
        {{ $t('EMAIL_HEADER.BCC') }}:
        {{ bccEmail.join(', ') }}
      </div>
    </div>
    <div class="flex flex-col items-end flex-shrink-0 gap-1">
      <span
        class="px-1.5 py-0.5 font-medium tracking-wide uppercase rounded text-xxs"
        :class="
          isOutgoing
            ? 'bg-n-blue-3 text-n-blue-11'
            : 'bg-n-alpha-2 text-n-slate-11'
        "
      >
        {{
          isOutgoing
            ? $t('CONVERSATION.EMAIL_VIEW.SENT')
            : $t('CONVERSATION.EMAIL_VIEW.RECEIVED')
        }}
      </span>
      <MessageMeta class="text-n-slate-11" />
    </div>
  </section>
</template>
